import { Button } from "@hex-insights/core";
import { AddSubFormButtonRenderProps, Form, SubFormField, SubFormRenderProps, TextField } from "@hex-insights/forms";
import {
	CourseAssessmentCategoryMarkPortionFormValues,
	CourseSectionFormValues,
	CourseSectionPeriodFormValues,
	StudentCourseSectionEnrollmentFormValues,
	TeacherCourseSectionEnrollmentFormValues,
} from "../../FormUtilities";
import { AcademicTermSelectField } from "../AcademicTerm";
import { CourseSelectField } from "../Course";
import { CourseAssessmentCategoryMarkPortionForm } from "../CourseAssessmentCategoryMarkPortion";
import { CourseSectionPeriodForm } from "../CourseSectionPeriod";
import { HomeRoomSectionSelectField } from "../HomeRoomSection";
import { BaseFieldProps } from "../shared";
import { StudentCourseSectionEnrollmentForm } from "../StudentCourseSectionEnrollment";
import { TeacherCourseSectionEnrollmentForm } from "../TeacherCourseSectionEnrollment";

/**
 * Generic props for fields of the CourseSection model.
 */
type FieldProps<K extends keyof CourseSectionFormValues.Base = keyof CourseSectionFormValues.Base> = BaseFieldProps<
	Pick<CourseSectionFormValues.Base, K>
>;

/**
 * Generic props for fields of the CourseSection model that only appear in the create form.
 */
type CreateFieldProps<K extends keyof CourseSectionFormValues.Create = keyof CourseSectionFormValues.Create> =
	BaseFieldProps<Pick<CourseSectionFormValues.Create, K>>;

/**
 * Field component for the `namePrefix` field of the CourseSection model.
 */
export function NamePrefix({ formState, ...props }: FieldProps<"namePrefix">) {
	return <TextField formState={formState} name="namePrefix" optional allowTrailingWhitespace {...props} />;
}

/**
 * Field component for the `nameSuffix` field of the CourseSection model.
 */
export function NameSuffix({ formState, ...props }: FieldProps<"nameSuffix">) {
	return <TextField formState={formState} name="nameSuffix" optional allowLeadingWhitespace {...props} />;
}

export namespace AcademicTerm {
	export type SelectProps = FieldProps<"academicTermID"> &
		Omit<AcademicTermSelectField.SelectProps<CourseSectionFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `academicTerm` edge of the CourseSection model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <AcademicTermSelectField.Select formState={formState} name="academicTermID" {...props} />;
	}
}

export namespace Course {
	export type SelectProps = FieldProps<"courseID"> &
		Omit<CourseSelectField.SelectProps<CourseSectionFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `course` edge of the CourseSection model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <CourseSelectField.Select formState={formState} name="courseID" {...props} />;
	}
}

export namespace CourseAssessmentCategoryMarkPortions {
	/**
	 * SubForm field component for the `courseAssessmentCategoryMarkPortions` edge of the CourseSection model.
	 */
	export function SubForm({ formState, ...props }: CreateFieldProps<"courseAssessmentCategoryMarkPortions">) {
		return (
			<SubFormField
				formState={formState}
				name="courseAssessmentCategoryMarkPortions"
				blankItem={CourseAssessmentCategoryMarkPortionFormValues.blankItemInCourseSection}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({
		formState,
		onRemoveClick,
	}: SubFormRenderProps<CourseAssessmentCategoryMarkPortionFormValues.CreateInCourseSection>) {
		return (
			<Form.Column>
				<Button variant="tertiary" size="small" onClick={onRemoveClick} disabled={formState.disabled}>
					Remove Course Assessment Category Mark Portion
				</Button>
				<CourseAssessmentCategoryMarkPortionForm.CreateFieldsInCourseSection formState={formState} />
			</Form.Column>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
	}: AddSubFormButtonRenderProps<
		Pick<CourseSectionFormValues.Create, "courseAssessmentCategoryMarkPortions">,
		CourseAssessmentCategoryMarkPortionFormValues.CreateInCourseSection
	>) {
		return (
			<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
				Add Course Assessment Category Mark Portion
			</Button>
		);
	};
}

export namespace CourseSectionPeriods {
	/**
	 * SubForm field component for the `courseSectionPeriods` edge of the CourseSection model.
	 */
	export function SubForm({ formState, ...props }: CreateFieldProps<"courseSectionPeriods">) {
		return (
			<SubFormField
				formState={formState}
				name="courseSectionPeriods"
				blankItem={CourseSectionPeriodFormValues.blankItemInCourseSection}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({
		formState,
		onRemoveClick,
	}: SubFormRenderProps<CourseSectionPeriodFormValues.CreateInCourseSection>) {
		return (
			<Form.Column>
				<Button variant="tertiary" size="small" onClick={onRemoveClick} disabled={formState.disabled}>
					Remove Course Section Period
				</Button>
				<CourseSectionPeriodForm.CreateFieldsInCourseSection formState={formState} />
			</Form.Column>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
	}: AddSubFormButtonRenderProps<
		Pick<CourseSectionFormValues.Create, "courseSectionPeriods">,
		CourseSectionPeriodFormValues.CreateInCourseSection
	>) {
		return (
			<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
				Add Course Section Period
			</Button>
		);
	};
}

export namespace HomeRoomSection {
	export type SelectProps = FieldProps<"homeRoomSectionID"> &
		Omit<HomeRoomSectionSelectField.SelectProps<CourseSectionFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `homeRoomSection` edge of the CourseSection model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <HomeRoomSectionSelectField.Select formState={formState} name="homeRoomSectionID" optional {...props} />;
	}
}

export namespace StudentCourseSectionEnrollments {
	/**
	 * SubForm field component for the `studentCourseSectionEnrollments` edge of the CourseSection model.
	 */
	export function SubForm({ formState, ...props }: CreateFieldProps<"studentCourseSectionEnrollments">) {
		return (
			<SubFormField
				formState={formState}
				name="studentCourseSectionEnrollments"
				blankItem={StudentCourseSectionEnrollmentFormValues.blankItemInCourseSection}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({
		formState,
		onRemoveClick,
	}: SubFormRenderProps<StudentCourseSectionEnrollmentFormValues.CreateInCourseSection>) {
		return (
			<Form.Column>
				<Button variant="tertiary" size="small" onClick={onRemoveClick} disabled={formState.disabled}>
					Remove Student Course Section Enrollment
				</Button>
				<StudentCourseSectionEnrollmentForm.CreateFieldsInCourseSection formState={formState} />
			</Form.Column>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
	}: AddSubFormButtonRenderProps<
		Pick<CourseSectionFormValues.Create, "studentCourseSectionEnrollments">,
		StudentCourseSectionEnrollmentFormValues.CreateInCourseSection
	>) {
		return (
			<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
				Add Student Course Section Enrollment
			</Button>
		);
	};
}

export namespace TeacherCourseSectionEnrollments {
	/**
	 * SubForm field component for the `teacherCourseSectionEnrollments` edge of the CourseSection model.
	 */
	export function SubForm({ formState, ...props }: CreateFieldProps<"teacherCourseSectionEnrollments">) {
		return (
			<SubFormField
				formState={formState}
				name="teacherCourseSectionEnrollments"
				blankItem={TeacherCourseSectionEnrollmentFormValues.blankItemInCourseSection}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({
		formState,
		onRemoveClick,
	}: SubFormRenderProps<TeacherCourseSectionEnrollmentFormValues.CreateInCourseSection>) {
		return (
			<Form.Column>
				<Button variant="tertiary" size="small" onClick={onRemoveClick} disabled={formState.disabled}>
					Remove Teacher Course Section Enrollment
				</Button>
				<TeacherCourseSectionEnrollmentForm.CreateFieldsInCourseSection formState={formState} />
			</Form.Column>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
	}: AddSubFormButtonRenderProps<
		Pick<CourseSectionFormValues.Create, "teacherCourseSectionEnrollments">,
		TeacherCourseSectionEnrollmentFormValues.CreateInCourseSection
	>) {
		return (
			<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
				Add Teacher Course Section Enrollment
			</Button>
		);
	};
}
